import { Controller } from '@hotwired/stimulus';
import {
  Chart,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js';

Chart.register(Filler, LineController, LineElement, LinearScale, PointElement, Tooltip);

// Connects to data-controller="ticket-price-trend"
export default class extends Controller {
  static targets = [
    'canvas',
    'currentLowestPrice',
    'movement',
    'movementLabel',
    'priceRange',
    'rangeButton',
    'rangeLabel',
    'updatedAt'
  ];

  static values = {
    data: Object,
    defaultRange: String
  };

  connect() {
    this.priceFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.dataValue.currency,
      maximumFractionDigits: 0
    });
    this.dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    });
    this.dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });

    this.updateRangeAvailability();
    this.renderRange(this.defaultRangeValue);
  }

  disconnect() {
    this.chart?.destroy();
  }

  selectRange({ params: { range } }) {
    const button = this.rangeButtonTargets.find(element => element.dataset.ticketPriceTrendRangeParam === range);
    if (button?.disabled) return;

    this.renderRange(range);
  }

  renderRange(range) {
    const points = this.pointsFor(range);
    if (points.length < 2) return;

    this.rangeButtonTargets.forEach((button) => {
      const selected = button.dataset.ticketPriceTrendRangeParam === range;
      button.setAttribute('aria-pressed', selected.toString());
    });

    this.updateSummary(points, range);
    this.renderChart(points);
  }

  updateRangeAvailability() {
    this.rangeButtonTargets.forEach((button) => {
      const range = button.dataset.ticketPriceTrendRangeParam;
      button.disabled = this.pointsFor(range).length < 2;
    });
  }

  pointsFor(range) {
    const points = this.dataValue.points;
    if (range === 'all') return points;

    const days = range === '7d' ? 7 : 30;
    const cutoff = points.at(-1).timestamp - (days * 24 * 60 * 60 * 1000);
    return points.filter(point => point.timestamp >= cutoff);
  }

  updateSummary(points, range) {
    const first = points[0];
    const last = points.at(-1);
    const prices = points.map(point => point.price);
    const rangeLabel = this.labelFor(range);
    const movement = this.movement(first.price, last.price);
    const priceRange = `${this.formatPrice(Math.min(...prices))}–${this.formatPrice(Math.max(...prices))}`;

    this.currentLowestPriceTarget.textContent = this.formatPrice(this.dataValue.current_lowest_price);
    this.movementLabelTarget.textContent = `${rangeLabel} change`;
    this.movementTarget.textContent = movement;
    this.rangeLabelTarget.textContent = `${rangeLabel} range`;
    this.priceRangeTarget.textContent = priceRange;
    this.updatedAtTarget.textContent = this.dateTimeFormatter.format(last.timestamp);
    this.canvasTarget.setAttribute(
      'aria-label',
      `${rangeLabel} ticket price trend. Current low ${this.formatPrice(this.dataValue.current_lowest_price)}. `
      + `${movement}. Prices ranged from ${priceRange}.`
    );
  }

  renderChart(points) {
    const styles = getComputedStyle(this.element);
    const lineColor = styles.getPropertyValue('--ticket-price-trend-line').trim();
    const fillColor = styles.getPropertyValue('--ticket-price-trend-fill').trim();
    const data = points.map(point => ({
      x: point.timestamp,
      y: point.price
    }));

    this.chart?.destroy();
    this.chart = new Chart(this.canvasTarget, {
      type: 'line',
      data: {
        datasets: [{
          data,
          borderColor: lineColor,
          backgroundColor: fillColor,
          borderWidth: 2,
          fill: true,
          pointHitRadius: 12,
          pointHoverRadius: 4,
          pointRadius: 0,
          tension: 0.2
        }]
      },
      options: {
        animation: false,
        maintainAspectRatio: false,
        normalized: true,
        parsing: false,
        interaction: {
          intersect: false,
          mode: 'nearest'
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: items => this.dateTimeFormatter.format(items[0].raw.x),
              label: item => this.formatPrice(item.raw.y)
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            grid: {
              display: false
            },
            ticks: {
              callback: value => this.dateFormatter.format(value),
              maxTicksLimit: 6
            }
          },
          y: {
            ticks: {
              callback: value => this.formatPrice(value),
              maxTicksLimit: 5
            }
          }
        }
      }
    });
  }

  movement(firstPrice, lastPrice) {
    const change = lastPrice - firstPrice;
    if (change === 0) return 'No change';

    const direction = change < 0 ? 'Down' : 'Up';
    const percentage = Math.round((Math.abs(change) / firstPrice) * 100);
    return `${direction} ${this.formatPrice(Math.abs(change))} (${percentage}%)`;
  }

  formatPrice(amount) {
    return this.priceFormatter.format(amount);
  }

  labelFor(range) {
    if (range === '7d') return '7-day';
    if (range === '30d') return '30-day';
    return 'All-time';
  }
}
